import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { validators, formats, dates } from 'investira.sdk';
import { IconButton, Tooltip, Stack, Icon, Switch, Chip } from 'investira.react.components';
import { BulkContextActions, DataTable } from '../';
import { MutedCopy } from '../../molecules';
import { CARTEIRA_TIPO } from '../../../enums';
import usePerfil from '../../../hooks/usePerfil';
import useEntidade from '../../../hooks/useEntidade';
import { addWorkingDays, startOf, toDate } from 'investira.sdk/lib/utils/dates';

const CarteirasDataGrid = props => {
  const {
    state,
    output_id,
    onFavClick,
    onEditClick,
    onBulkDeleteClick,
    onDeleteClick,
    onSwitchChange,
    renderDetailPanel,
    data,
    ...restProps
  } = props;

  const { config } = useEntidade();

  const { usuarioPrioridade } = usePerfil();

  // Estado local para controlar os switches (update otimista)
  const [switchStates, setSwitchStates] = useState({});

  // Sincroniza o estado local com os dados iniciais
  useEffect(() => {
    if (validators.isNull(data) || !Array.isArray(data)) {
      return;
    }

    const xStates = {};

    data.forEach(xRow => {
      const xCarteiraId = xRow.carteira_id;
      const xCarteiraConfig = xRow.carteira_config;
      const xValue =
        xCarteiraConfig === null || xCarteiraConfig === undefined
          ? true
          : xCarteiraConfig?.custodiante_conferencia ?? false;
      xStates[xCarteiraId] = Boolean(xValue);
    });

    setSwitchStates(xStates);
  }, [data]);

  /**
   * Verifica se a data está dentro do período de conferência
   */
  const isValidConferencia = pData => {
    const xDiasDelay = Number(config?.custodiante_conferencia_dias_delay || 0);
    const xDiasJanela = Number(config?.custodiante_conferencia_dias_janela || 0);
    const xAtraso = xDiasDelay + xDiasJanela;
    const xDataInicio = startOf('day', addWorkingDays(toDate(), -xAtraso));
    const xDataFim = addWorkingDays(xDataInicio, xDiasJanela);
    if (toDate(pData) < toDate(xDataInicio) || toDate(pData) > toDate(xDataFim)) {
      return false;
    }
    return true;
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'carteira',
        header: 'Carteira',
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: {
            width: '100% !important'
          }
        },
        Cell: ({ cell, row }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return (
            <MutedCopy title="Copiar ID" value={row.original.carteira_id}>
              {cell.getValue()}
            </MutedCopy>
          );
        }
      },
      {
        accessorKey: 'carteira_seuid',
        header: 'ISIN/Código',
        enableColumnActions: false,
        enableSorting: false
      },
      {
        accessorKey: 'data_posicao',
        header: 'Última Posição',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        }
      },
      {
        accessorKey: 'carteira_tipo_id',
        header: 'Tipo',
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return CARTEIRA_TIPO[cell.getValue()]?.label;
        }
      },
      // Só adiciona a coluna se config.custodiante_conferencia for true
      ...(config?.custodiante_conferencia
        ? [
            {
              accessorKey: 'carteira_config.custodiante_conferencia',
              header: 'Conferência',
              enableColumnActions: false,
              enableSorting: false,
              muiTableBodyCellProps: {
                align: 'center'
              },
              muiTableHeadCellProps: {
                align: 'center'
              },
              Cell: ({ cell, row }) => {
                const xConferenciaCustodiantePendente =
                  row.original?.conferencia_custodiante_pendente; // 0 | 1 | null
                const xCustodianteCarteiraId = row.original?.custodiante_carteira_id; // hash | null

                if (
                  xCustodianteCarteiraId === null ||
                  !isValidConferencia(row.original.data_posicao)
                ) {
                  return null;
                }

                if (xConferenciaCustodiantePendente === 0) {
                  return (
                    <Chip
                      icon={<Icon iconName="check" size={18} />}
                      label="Conferido"
                      color="primary"
                      size="small"
                      sx={{ px: 1 }}
                    />
                  );
                }

                if (xConferenciaCustodiantePendente === 1) {
                  return (
                    <Chip
                      icon={<Icon iconName="synchronize" size={18} />}
                      label="Processando"
                      color="warning"
                      size="small"
                      sx={{ px: 1 }}
                    />
                  );
                }

                return null;
              }
            }
          ]
        : [])
    ],
    [onSwitchChange, switchStates, config?.custodiante_conferencia]
  );

  const xProps = {
    columns,
    data,
    state,
    enableRowSelection: usuarioPrioridade <= 6,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: ''
      }
    },
    renderTopToolbarCustomActions: ({ table }) => {
      if (usuarioPrioridade > 6) {
        return null;
      }

      const selectedRows = table.getSelectedRowModel().rows;
      const isSomeRowSelected = selectedRows.length > 0;

      return (
        <BulkContextActions
          onDeleteClick={() => {
            onBulkDeleteClick(selectedRows);
          }}
          disabled={state.isLoading || state.showProgressBars || !isSomeRowSelected}
        />
      );
    },
    renderRowActions: ({ row }) => {
      const { carteira_id, favorito } = row.original;
      return (
        <Stack direction="row" spacing={2}>
          <Tooltip title="Visualizar Posições">
            <IconButton color="primary" onClick={() => onEditClick(row.original)}>
              <Icon iconName="eye" size={21} />
            </IconButton>
          </Tooltip>

          {usuarioPrioridade <= 6 && (
            <Tooltip title="Favoritar">
              <IconButton
                color="primary"
                onClick={() => {
                  onFavClick &&
                    onFavClick({
                      carteira_id,
                      favorito: Boolean(favorito) ? 0 : 1
                    });
                }}>
                <Icon iconName={Boolean(favorito) ? 'star5_filled' : 'star5'} size={21} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      );
    },
    ...restProps
  };

  return <DataTable {...xProps} />;
};

CarteirasDataGrid.propTypes = {
  data: PropTypes.array.isRequired,
  state: PropTypes.object,
  initialState: PropTypes.object
};

export default CarteirasDataGrid;
